import { ENUM_UTILS, OpenPosition, UISerializableOrder } from '@drift/common';
import { AccountSpotBalance } from 'src/stores/useDriftAccountsStore';
import { MarginInfo } from 'src/stores/driftStoreTypes';
import { BorrowMarketData } from 'src/hooks/useSyncBorrowLendBalances';
import { TradeInfo } from 'src/utils/tradeRecord';
import {
	BASE_PRECISION_EXP,
	BN,
	BigNum,
	QUOTE_PRECISION_EXP,
	PRICE_PRECISION_EXP,
} from '@drift-labs/sdk';

export type PositionMarginInfo = {
	isIsolated: boolean;
	isolatedMargin: BN | null;
};

export function buildElfaUserData({
	authority,
	orders,
	positions,
	spotBalances,
	marginInfo,
	borrowLendData,
	predictedFundings,
	positionMarginInfoMap,
	recentTrades,
}: {
	authority: string;
	orders: UISerializableOrder[];
	positions: OpenPosition[];
	spotBalances: AccountSpotBalance[];
	marginInfo: MarginInfo | undefined;
	borrowLendData: BorrowMarketData[];
	predictedFundings: { marketIndex: number; fundingRate: number }[];
	positionMarginInfoMap: Map<number, PositionMarginInfo>;
	recentTrades: TradeInfo[];
}): Record<string, unknown> {
	return {
		authority,
		leverage: marginInfo?.leverage ?? null,
		freeCollateral: marginInfo?.freeCollateral
			? BigNum.from(marginInfo.freeCollateral, QUOTE_PRECISION_EXP).toNotional()
			: null,
		positions: positions.map((p) => {
			const predictedFundingRateVal = parseFloat(
				predictedFundings
					.find((f) => f.marketIndex === p.marketIndex)
					?.fundingRate.toFixed(5) ?? '0'
			);

			const posMarginInfo = positionMarginInfoMap.get(p.marketIndex);
			const isIsolated = posMarginInfo?.isIsolated ?? false;

			const notionalBn = BigNum.from(p.notional, QUOTE_PRECISION_EXP);
			let isolatedLeverage: string | null = null;
			if (isIsolated && posMarginInfo?.isolatedMargin) {
				const margin = BigNum.from(
					posMarginInfo.isolatedMargin,
					QUOTE_PRECISION_EXP
				);
				if (!margin.eqZero()) {
					isolatedLeverage = notionalBn.div(margin).toNotional();
				}
			}

			return {
				marketSymbol: p.marketSymbol,
				direction: p.direction,
				marginMode: isIsolated ? 'isolated' : 'cross',
				isolatedLeverage,
				notional: notionalBn.toNotional(),
				baseSize: BigNum.from(p.baseSize, BASE_PRECISION_EXP).toString(),
				entryPrice: BigNum.from(p.entryPrice, PRICE_PRECISION_EXP).toNotional(),
				liquidationPrice: BigNum.from(
					p.liqPrice,
					PRICE_PRECISION_EXP
				).toNotional(),
				pnlVsOracle: BigNum.from(
					p.pnlVsOracle,
					QUOTE_PRECISION_EXP
				).toNotional(),
				predictedHourlyFundingRate: predictedFundingRateVal,
			};
		}),
		orders: orders.map((o) => ({
			marketIndex: o.marketIndex,
			marketType: ENUM_UTILS.toStr(o.marketType),
			orderType: ENUM_UTILS.toStr(o.orderType),
			direction: ENUM_UTILS.toStr(o.direction),
			price: o.price.toNotional(),
			baseAssetAmount: o.baseAssetAmount.toNum(),
			triggerPrice:
				!o.triggerPrice || o.triggerPrice.eqZero()
					? null
					: o.triggerPrice.toNotional(),
			reduceOnly: o.reduceOnly,
		})),
		spotBalances: spotBalances.map((b) => {
			const marketData = borrowLendData.find(
				(d) => d.bankConfig.marketIndex === b.asset.marketIndex
			);
			return {
				symbol: b.asset.symbol,
				balance: b.balance.print(),
				balanceType: ENUM_UTILS.toStr(b.balanceType),
				depositApr: marketData?.depositApr.toNum() ?? null,
				borrowApr: marketData?.borrowApr.toNum() ?? null,
			};
		}),
		recentTrades: recentTrades.map((t) => ({
			marketIndex: t.marketIndex,
			marketType: t.marketType,
			direction: t.direction ? ENUM_UTILS.toStr(t.direction) : null,
			baseAmountFilled: t.baseAssetAmountFilled.toNum(),
			quoteAmountFilled: t.quoteAssetAmountFilled.toNum(),
			fee: t.fee?.toNum() ?? null,
			timestamp: t.ts.toNumber(),
		})),
	};
}
